import React from "react";
import { useQuery } from "@tanstack/react-query";
import { httpClient } from "common/httpClient";
import * as utils from "common/utils";
import { Panel, PanelBody } from "components/Panel";
import { SortableTable, SortableTableHeader } from "components/SortableTable";
import SelectPack from "components/SelectPack";
import ActionLink from "components/ActionLink";
import NoData from "components/NoData/NoData";

const ALL = "All";

export default function OrgChanges({ org }) {
    const { isLoading, error, data: changeLog } = useOrgChangeLog(org?.id);
    const hasData = changeLog?.length > 0;

    return (
        <Panel loading={isLoading} error={error}>
            <PanelBody loading={isLoading}>{hasData ? <OrgChangeLog changeLog={changeLog} /> : <NoData message="Nothing has been recorded for this organisation" />}</PanelBody>
        </Panel>
    );
}

function OrgChangeLog({ changeLog }) {
    const [commandName, setCommandName] = React.useState(ALL);
    const [expandedId, setExpandedId] = React.useState(null);

    // Built from the data, so an org only offers filters it actually has entries for and a new
    // command name recorded server side needs no change here.
    const actions = React.useMemo(() => {
        const byCommandName = new Map(changeLog.map((entry) => [entry.commandName, entry.action]));
        return [...byCommandName].sort((a, b) => a[1].localeCompare(b[1]));
    }, [changeLog]);

    const entries = changeLog.filter((entry) => commandName === ALL || entry.commandName === commandName);

    return (
        <>
            <div className="u-flex u-flexJustifyBetween u-flexAlignItemsCenter">
                <div className="h2">
                    {entries.length} {entries.length === 1 ? "entry" : "entries"}
                </div>
                <div className="u-mb-sm">
                    <SelectPack meta={{ nrf: true }} onChange={(e) => setCommandName(e.target.value)} value={commandName} id="commandName" label="Filter by action">
                        <option value={ALL}>All</option>
                        {actions.map(([value, label]) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </SelectPack>
                </div>
            </div>
            <SortableTable data={entries} defaultSortCriteria={{ fieldName: "changedOn", fieldType: "date", desc: true }} pageSize={25}>
                {({ data, sortCriteria, handleSort }) => (
                    <table>
                        <thead>
                            <tr>
                                <SortableTableHeader label="When" fieldName="changedOn" sortCriteria={sortCriteria} handleSort={handleSort} date shrink />
                                <SortableTableHeader label="By" fieldName="changedBy" sortCriteria={sortCriteria} handleSort={handleSort} />
                                <SortableTableHeader label="Action" fieldName="action" sortCriteria={sortCriteria} handleSort={handleSort} />
                                <th>Change</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((entry) => {
                                const changes = entry.changes || [];
                                const isExpanded = expandedId === entry.id;

                                return (
                                    <React.Fragment key={entry.id}>
                                        <tr className="item">
                                            <td className="u-textNoWrap">{utils.dateLong(entry.changedOn, "-")}</td>
                                            <td>
                                                {entry.changedBy || "Unknown"}
                                                {entry.changedByOrg && <span className="u-textLight u-ml-sm">{entry.changedByOrg}</span>}
                                            </td>
                                            <td>{entry.action}</td>
                                            <td>
                                                {changes.length > 0 && (
                                                    <ActionLink id={`toggle-changes-${entry.id}`} className="u-flex u-flexAlignItemsCenter" title={isExpanded ? "Hide what changed" : "Show what changed"} onClick={() => setExpandedId(isExpanded ? null : entry.id)}>
                                                        <span>
                                                            {changes.length} {changes.length === 1 ? "field" : "fields"}
                                                        </span>
                                                        <i className={`icon icon-arrow-${isExpanded ? "up" : "down"} u-ml-xs`} />
                                                    </ActionLink>
                                                )}
                                            </td>
                                        </tr>
                                        {isExpanded &&
                                            changes.map((change) => (
                                                <tr key={`${entry.id}-${change.field}`} className="tr-nested">
                                                    <td></td>
                                                    <td></td>
                                                    <td>{change.label}</td>
                                                    <td>
                                                        {/* No old value on a create, where there is nothing to show it changing from. */}
                                                        {change.oldValue != null && (
                                                            <>
                                                                <span className="u-textLight">{change.oldValue}</span>
                                                                <i className="icon icon-arrow-right u-ml-xs u-mr-xs" />
                                                            </>
                                                        )}
                                                        <b>{change.newValue}</b>
                                                    </td>
                                                </tr>
                                            ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </SortableTable>
        </>
    );
}

function useOrgChangeLog(orgId) {
    const query = useQuery({
        queryKey: ["orgs", orgId, "changes"],
        queryFn: async () => httpClient.get(`admin/accounts/${orgId}/changes`),
        retry: false,
    });

    return {
        isLoading: query.isLoading,
        error: query.error?.message,
        data: query.data,
    };
}
